import type ItineraryOrder from '../ItineraryOrder';
import {
    type Point,
    SortDirection,
    type TripFilters,
    type TripSort,
    TripSortBy,
} from './TripModuleTypes';
import { type TaxiLateralLoader } from '@itinerary/trip/bus/TaxiLateralLoader';
import type Trip from '@itinerary/trip/Trip';
import type TripPackage from '@itinerary/trip/TripPackage';
import type TripPackageSet from '@itinerary/trip/TripPackageSet';
import type TripPriceClass from '@itinerary/trip/TripPriceClass';
import {
    type EmptyListReason,
    type SortBy,
} from '@itinerary/ItineraryModuleTypes';
import ItineraryModule from '@itinerary/ItineraryModule';
import { type TripSearchForm } from '@search/trip/TripSearchForm';
import { type Location } from '@utils/types';
import { ReserveType } from '@utils/itinerary';
import { TripType, type TripUrlParams } from '@utils/trip';
import { type TripPackageLoaderConfig } from '@itinerary/trip/types';
import { TrackingType } from '@itinerary/types';

export default abstract class TripModule<
    TTrip extends Trip = Trip,
    TPrice extends TripPriceClass = TripPriceClass,
    TFilters extends TripFilters = TripFilters,
    TLocation extends Location<TLocation> = Location,
    TPackage extends TripPackage<TTrip, TPrice> = TripPackage<TTrip, TPrice>,
    TPackageSet extends TripPackageSet<
        TFilters['meta'],
        TFilters['key'],
        TPackage
    > = TripPackageSet<TFilters['meta'], TFilters['key'], TPackage>,
> extends ItineraryModule<TPrice, TFilters, TLocation, TPackage, TPackageSet> {
    //#region state
    /**
     * Indicates the variable to sort trip packages by
     *
     * @type {TripSortBy}
     * @memberof TripModule
     */
    sortBy: TripSort = TripSortBy.Price;
    //#endregion

    override get emptyListReason(): EmptyListReason {
        return {
            image: `/img/no-result/${this.reserveInfo.type}.svg`,
            title: 'در تاریخ و مسیر مورد نظر',
            notFoundText: `${this.reserveInfo.nameE} موجود نیست!`,
        };
    }

    override get supportedTrackingTypes() {
        if (this.areAllPackagesFull) {
            return [TrackingType.Capacity];
        }
        return [TrackingType.Price];
    }

    private get searchOrigin(): Location | null {
        const currentSearchForm = useCurrentSearchForm<TripSearchForm>();
        return currentSearchForm.value.origin;
    }

    private get searchDestination(): Location | null {
        const currentSearchForm = useCurrentSearchForm<TripSearchForm>();
        return currentSearchForm.value.destination;
    }

    get originName(): string {
        let originName =
            this.currentPackageSet?.originName ?? this.searchOrigin?.title;
        originName = originName?.replace(/(.+) - (?:پایانه )?(.+)/, '$1 ($2)');
        return originName ?? '';
    }

    get destinationName(): string {
        let destinationName =
            this.currentPackageSet?.destinationName ??
            this.searchDestination?.title;
        destinationName = destinationName?.replace(
            /(.+) - (?:پایانه )?(.+)/,
            '$1 ($2)'
        );
        return destinationName ?? '';
    }

    get originCurrentCode(): string {
        const originCode =
            this.currentPackageSet?.originCode ?? this.searchOrigin?.code;
        return originCode ?? '';
    }

    get destinationCurrentCode(): string {
        const destinationCode =
            this.currentPackageSet?.destinationCode ??
            this.searchDestination?.code;
        return destinationCode ?? '';
    }

    override get mainTitle() {
        if (!this.originName || !this.destinationName) {
            return '';
        }
        return `بلیط  ${this.reserveInfo.title} ${this.originName.replace(
            '(همه فرودگاه‌ها)',
            ''
        )} به ${this.destinationName.replace('(همه فرودگاه‌ها)', '')}`;
    }

    override get dateTitle() {
        const { departureDate, returnDate } =
            useCurrentSearchForm<TripSearchForm>().value;
        const currentDate = this.currentSetIndex ? returnDate : departureDate;
        if (!currentDate) {
            return '';
        }
        return dayConfig
            .js(currentDate)
            .format(formatter({ format: 'long', includeWeekday: true }));
    }

    override get sortByList(): SortBy[] {
        return [
            {
                type: TripSortBy.Time,
                label: 'زود‌ترین زمان',
            },
            {
                type: TripSortBy.Price,
                label: 'کمترین قیمت',
            },
        ];
    }

    sortInfo(sortType: TripSort) {
        const [sortBy, sortDir = SortDirection.Asc] = sortType.split('-');

        return {
            sortBy,
            sortDir,
        } as {
            sortBy: TripSortBy;
            sortDir: SortDirection;
        };
    }

    sortHandler(a: ItineraryOrder, b: ItineraryOrder, sortType?: TripSort) {
        const { sortBy, sortDir } = this.sortInfo(sortType ?? this.sortBy);

        if (sortDir === SortDirection.Asc) {
            return a[sortBy] - b[sortBy];
        } else {
            return b[sortBy] - a[sortBy];
        }
    }

    override applySort(list: TPackage[]): TPackage[] {
        return list.concat().sort((a, b) => {
            if (a.pinToTop && b.pinToTop) {
                // do nothing, continue to normal sort below
            } else if (a.pinToTop) {
                return -1;
            } else if (b.pinToTop) {
                return 1;
            }
            if (this.isInitialSort) {
                if (a.isSpecialOffer && b.isSpecialOffer) {
                    // do nothing, continue sort below
                }
                if (a.isSpecialOffer) {
                    return -1;
                }
                if (b.isSpecialOffer) {
                    return 1;
                }
            }

            const defaultSort =
                this.sortHandler(a.order, b.order) ||
                (a.order.customOrder &&
                    b.order.customOrder &&
                    a.order.customOrder(a) - b.order.customOrder(b)) ||
                0;
            if (defaultSort) {
                return defaultSort;
            }
            const alternativeSorts = EnumClass.create(TripSortBy)
                .getValues()
                .filter(sort => sort !== this.sortBy)
                .filter(sort =>
                    this.sortByList.map(sort => sort.type).includes(sort)
                );
            for (const sort of alternativeSorts) {
                const sortResult = this.sortHandler(a.order, b.order, sort);
                if (sortResult) {
                    return sortResult;
                }
            }
            return 0;
        });
    }

    override get sortedReservableList(): TPackage[] {
        return this.applySort(this.list.filter(pck => pck.reservable));
    }

    get routePackageList() {
        return this.currentPackageSet?.routePackages || [];
    }

    get isLoadingRoutePackages() {
        return this.currentPackageSet?.isLoadingRoutePackages;
    }

    get isRouteEnabled() {
        return !!this.currentPackageSet?.isRouteEnabled;
    }

    override get sortedNonReservableList(): TPackage[] {
        return this.applySort(this.list.filter(pck => !pck.reservable));
    }

    get originLocation(): Point | null {
        return this.currentPackageSet?.originLocation || null;
    }

    get destinationLocation(): Point | null {
        return this.currentPackageSet?.destinationLocation || null;
    }

    get isRoundTrip() {
        return this.expectedPackagesToBeSelected > 1;
    }

    abstract override getPackageSet(
        loaderConfig: TripPackageLoaderConfig
    ): TPackageSet;

    get lateralLoader(): TaxiLateralLoader | null {
        return null;
    }

    abstract override getInitialFilters(): { [key in TFilters['key']]: any };

    get averagePrice(): number {
        const searchFormStore = useSearchFormStore();
        const currentVehicle =
            searchFormStore.reserveType === ReserveType.Flight
                ? 'plane'
                : searchFormStore.reserveType;
        const list = this.list.filter(
            p => p.vehicleType === currentVehicle && p.reservable
        );
        return (
            Math.round(
                list.reduce(
                    (prev, curr) =>
                        prev + (curr.defaultPrice?.singlePayable ?? 0),
                    0
                ) /
                    list.filter(p => p.defaultPrice?.singlePayable).length /
                    10000
            ) * 10000
        );
    }

    override get list(): TPackage[] {
        if (this.isSelectingReturn) {
            const previousSelectedPackage = usePackageSelectStore()
                .selectedPackages[0] as TPackage;

            return (super.list as TPackage[]).filter(
                res =>
                    res.departureDateTime.getTime() >
                    (previousSelectedPackage?.arrivalDateTime.getTime() ?? 0) +
                        3 * 60 * 60 * 1000
            );
        }
        return super.list as TPackage[];
    }

    override get emptyPackage(): TPackage {
        throw new Error('Not implemented!');
    }

    override get expectedPackagesToBeSelected(): number {
        const currentSearchForm =
            useSearchFormStore().getFormModule<TripSearchForm>(
                this.reserveInfo.type
            );
        return currentSearchForm.tripType === TripType.OneWay ? 1 : 2;
    }

    override get description(): string | null {
        return this.descriptionTemplate
            .replace(/\(مبدا\)/g, this.originName)
            .replace(/\(مقصد\)/g, this.destinationName);
    }

    abstract override getItineraryPackageLoaderConfigs(
        params: TripUrlParams
    ): TripPackageLoaderConfig[];

    override async loadAvailable(params: {
        index: number;
        inForm?: boolean;
    }): Promise<void> {
        await super.loadAvailable(params);
        const currentSearchForm =
            useSearchFormStore().getFormModule<TripSearchForm>(
                this.reserveInfo.type
            );
        if (currentSearchForm.departureDate && !params.inForm) {
            currentSearchForm.loadPrices({
                until: dayConfig
                    .gregory(currentSearchForm.departureDate)
                    .add(7, 'day')
                    .startOf('day')
                    .toDate(),
            });
        }
    }

    override async loadLaterals() {
        if (
            !this.currentPackageSet?.date ||
            !this.currentPackageSet?.originCode ||
            !this.currentPackageSet?.destinationCode
        ) {
            return;
        }
        try {
            const currentSearchForm =
                useSearchFormStore().getFormModule<TripSearchForm>(
                    this.reserveInfo.type
                );
            const result = await this.lateralLoader?.loadItineraries({
                originCode: this.currentPackageSet.originCode,
                destinationCode: this.currentPackageSet.destinationCode,
                date: this.currentPackageSet.date,
                count: currentSearchForm.adultCount,
            });
            this.lateralPackage =
                result?.slice().sort((a, b) => {
                    if (
                        a.departureDateTime.getTime() ===
                        b.departureDateTime.getTime()
                    ) {
                        return a.singlePayable - b.singlePayable;
                    }
                    return (
                        a.departureDateTime.getTime() -
                        b.departureDateTime.getTime()
                    );
                })?.[0] || null;
        } catch (e) {
            reportException(e);
        }
    }
}
